import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import PlayerPickerOption from './PlayerPickerOption'
import { playerFullName } from '../../../../group_leader/helpers/player'
import { useListKeyboardNav } from '../../../hooks/useListKeyboardNav'

// The searchable list behind the place / blind / swap pickers; local state, as the list is already loaded (REACT-092).

const optionId = (index) => `gl-picker-opt-${index}`

const PlayerPickerList = ({
  options, onSelect, disabled = false,
  searchPlaceholder, searchAria, noMatchesText, emptyText = null,
  headerAction = null, onCancel, cancelLabel,
}) => {
  const [ query, setQuery ] = useState('')
  const listRef = useRef(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) { return options }
    return options.filter((player) => {
      const name = playerFullName(player).toLowerCase()
      return name.indexOf(q) !== -1
    })
  }, [ options, query ])

  const { active, reset, onKeyDown } = useListKeyboardNav(filtered, {
    onPick: (player) => { if (!disabled) { onSelect(player) } },
    onEscape: (event) => { event.preventDefault(); onCancel() },
  })

  const emptyMessage = options.length === 0 ? (emptyText || noMatchesText) : noMatchesText

  return (
    <div className="gl-event-setup-picker">
      <input
        type="text"
        className="gl-event-setup-picker-search"
        autoFocus
        value={query}
        placeholder={searchPlaceholder}
        aria-label={searchAria}
        role="combobox"
        aria-expanded="true"
        aria-controls="gl-picker-listbox"
        aria-activedescendant={active >= 0 ? optionId(active) : undefined}
        onChange={(event) => { setQuery(event.target.value); reset() }}
        onKeyDown={onKeyDown}
      />
      <div id="gl-picker-listbox" ref={listRef} className="gl-event-setup-picker-options" role="listbox">
        {filtered.length === 0 ? (
          <div className="gl-event-setup-picker-empty">{emptyMessage}</div>
        ) : filtered.map((player, index) => (
          <PlayerPickerOption
            key={player.player_round_id || player.id}
            id={optionId(index)}
            active={index === active}
            player={player}
            disabled={disabled}
            onClick={() => onSelect(player)}
          />
        ))}
      </div>
      {headerAction}
      <button className="gl-event-setup-slot-picker-cancel" type="button" onClick={onCancel}>
        {cancelLabel}
      </button>
    </div>
  )
}

PlayerPickerList.propTypes = {
  options: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  searchPlaceholder: PropTypes.string.isRequired,
  searchAria: PropTypes.string.isRequired,
  noMatchesText: PropTypes.string.isRequired,
  emptyText: PropTypes.string,
  headerAction: PropTypes.node,
  onCancel: PropTypes.func.isRequired,
  cancelLabel: PropTypes.string.isRequired,
}

export default PlayerPickerList
